/**
 * SnoozedView
 *
 * Virtual folder showing snoozed messages grouped by return date.
 */

import { useMemo } from 'react';
import { Clock, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useSnooze } from '../hooks/useSnooze';
import type { EmailSnoozedMessage } from '@/lib/api';

interface SnoozedViewProps {
  accountId: string | undefined;
}

function formatReturnTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function groupByDate(
  items: EmailSnoozedMessage[],
): { label: string; items: EmailSnoozedMessage[] }[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const endOfWeek = new Date(today);
  endOfWeek.setDate(endOfWeek.getDate() + (7 - endOfWeek.getDay()));
  const endOfNextWeek = new Date(endOfWeek);
  endOfNextWeek.setDate(endOfNextWeek.getDate() + 7);

  const groups: { label: string; items: EmailSnoozedMessage[] }[] = [
    { label: 'Today', items: [] },
    { label: 'Tomorrow', items: [] },
    { label: 'Later this week', items: [] },
    { label: 'Next week', items: [] },
    { label: 'Later', items: [] },
  ];

  for (const item of items) {
    const d = new Date(item.snoozeUntil);
    if (d < tomorrow) groups[0].items.push(item);
    else if (d < new Date(tomorrow.getTime() + 86400000)) groups[1].items.push(item);
    else if (d < endOfWeek) groups[2].items.push(item);
    else if (d < endOfNextWeek) groups[3].items.push(item);
    else groups[4].items.push(item);
  }

  return groups.filter((g) => g.items.length > 0);
}

export function SnoozedView({ accountId }: SnoozedViewProps) {
  const { snoozed, loading, cancelSnooze, returnDue } = useSnooze(accountId);

  const groups = useMemo(() => groupByDate(snoozed), [snoozed]);

  if (loading) {
    return (
      <div className="p-4 space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (snoozed.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-20">
        <Clock className="h-12 w-12 mb-3 opacity-40" />
        <p className="text-sm font-medium">No snoozed messages</p>
        <p className="text-xs mt-1">Snoozed messages will appear here</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <Clock className="h-4 w-4" /> Snoozed
          <Badge variant="secondary" className="text-xs">
            {snoozed.length}
          </Badge>
        </h2>
        <Button variant="outline" size="sm" onClick={returnDue} className="text-xs">
          Return due
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {groups.map((group) => (
          <div key={group.label}>
            <div className="px-4 py-1.5 bg-muted/50 text-xs font-medium text-muted-foreground border-b border-border">
              {group.label}
            </div>
            {group.items.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between px-4 py-3 border-b border-border hover:bg-muted/50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm">Message {item.messageId.substring(0, 8)}...</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                    <Clock className="h-3 w-3" />
                    Returning {formatReturnTime(item.snoozeUntil)}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs gap-1"
                  onClick={() => cancelSnooze(item.id)}
                >
                  <X className="h-3 w-3" /> Cancel
                </Button>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
